import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import _ from "lodash";

import { ACTION_BY_FILTERS_KEY } from "@/Constants/common";
import {
  _addClientUser,
  _deleteClientUser,
  _fetchClientContactAllList,
  _fetchClientUsers,
  _fetchClientUsersEdit,
  _updateClientUser,
  _resendEmailToClientUsers,
} from "@/services/clientusers";
import { handleAPICatch, showSuccessToast } from "@/utilities/utils";

import { fetchUserAccessCounts } from "./userAccess";
import { formatFetchUserListing } from "../../utilities/dataFormatter";

const initialState = {
  isLoading: false,
  isPaginationLoading: false,
  isClientContactAllLoading: true,
  clientUsers: [],
  clientContactAll: [],
  showAddNewUserModal: false,
  showEditNewUserModal: false,
  showPrimaryModal: false,
  EditClientUserData: {},
  deleteClientUserdata: {},
  selectedClientUser: [],

  filters: {
    search: "",
    userFirstName: "",
    userLastName: "",
    userPhoneNo: "",
    userRole: [],
    userEmail: "",
    userRefNo: "",
    userDirectdial: "",
    ...ACTION_BY_FILTERS_KEY,
  },
  sorting: {
    sortByColumn: "",
    sortByOrder: "",
  },
  totalCount: 0,
  isSelectedAll: false,
  oldFilters: {},
  pagination: {
    currentPage: 1,
    totalPage: 1,
    isLastPage: false,
  },
  toggleSelected: false,
  showFilters: false,
};

//Fetch user listing
export const fetchClientUsers = createAsyncThunk(
  "clientUsers/fetchClientUsers",
  async (data, { dispatch, getState }) => {
    const {
      clientUsers: { filters, pagination, sorting, clientUsers },
    } = getState();
    if (_.isEmpty(data)) {
      data = {
        ...filters,
      };
    }
    if (pagination.isLastPage) {
      return dispatch(setClientUsersLoading(false));
    }

    dispatch(setPaginationLoading(pagination.currentPage !== 1));
    dispatch(toggleSelectedAll(false));
    const _data = {
      ...data,
      currentPage: pagination.currentPage,
      ...sorting,
    };

    const response = _fetchClientUsers(_data);
    response
      .then((res) => {
        const {
          data: { client_users_list, meta },
        } = res;

        const { currentPage, lastPage, isLastPage, total: totalCount } = meta;

        let data = formatFetchUserListing(client_users_list);
        dispatch(
          updatePagination({
            currentPage: currentPage + 1,
            totalPage: lastPage,
            isLastPage: currentPage === lastPage,
          }),
        );
        let prevList = currentPage === 1 ? [] : _.uniq(clientUsers);
        dispatch(updateTotalCount(totalCount));
        let first_user_id = data.length > 0 ? data[0].user_id : null;
        if (
          prevList.length > 0 &&
          prevList.find((item) => item.user_id === first_user_id) !== undefined
        ) {
          //
        } else {
          dispatch(updateClientUsers([...prevList, ...data]));
        }
      })
      .catch((error) => {
        handleAPICatch(error);
      })
      .finally(() => {
        setTimeout(() => {
          dispatch(setClientUsersLoading(false));
          dispatch(setPaginationLoading(false));
        }, 10);
      });
  },
);

//Add client user
export const addClientUserData = createAsyncThunk(
  "clientUsers/addClientUserData",
  async (data, { dispatch, getState }) => {
    const { values, isSubmitting, setIsSubmitting, avatarFile } = data;
    if (isSubmitting) {
      return;
    }
    setIsSubmitting(true);
    try {
      let response = await _addClientUser(values, avatarFile);
      // console.log(response);
      const { message } = response;
      dispatch(deSelectAllUsers());
      dispatch(fetchUserAccessCounts());
      dispatch(updatePagination({ currentPage: 1, isLastPage: false }));
      dispatch(setClientUsersLoading(true));
      dispatch(fetchClientUsers());
      dispatch(toggleAddNewUserModal(false));
      showSuccessToast(message);
      if (getState().clientProfile.user.is_primary_contact === 1) {
        dispatch(fetchClientContactAlldata());
      }
    } catch (error) {
      handleAPICatch(error);
    } finally {
      setIsSubmitting(false);
    }
  },
);

export const editClientUserData = createAsyncThunk(
  "clientUsers/editClientUserData",
  async (data, { dispatch, getState }) => {
    const {
      values,
      setSubmitting,
      isSubmitting,
      setIsSubmitting,
      callback,
      avatarFile,
      EditClientUserData,
    } = data;
    if (isSubmitting) {
      return;
    }
    setIsSubmitting(true);
    let response = _updateClientUser(values, avatarFile, EditClientUserData);
    response
      .then((res) => {
        const { message } = res;
        //callback - fetch user listing again and comes from user Action page.
        callback && callback();
        dispatch(toggleEditNewUserModal(false));
        showSuccessToast(message);
      })
      .catch((error) => {
        handleAPICatch(error);
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  },
);

// edit user
export const fetchClientUserEditdata = createAsyncThunk(
  "clientUsers/fetchClientUserEditdata",
  async (data, { dispatch }) => {
    const { loading, setLoading, id } = data;
    if (loading) return;
    setLoading(true);

    const response = _fetchClientUsersEdit(id);
    response
      .then((res) => {
        const {
          data: { user_detail, message },
        } = res;
        dispatch(
          updateClientUserEdit({
            ...user_detail,
          }),
        );
        dispatch(toggleEditNewUserModal(true));
        dispatch(deSelectAllUsers());
      })
      .catch((error) => {
        handleAPICatch(error);
      })
      .finally(() => {
        setLoading(false);
      });
  },
);

export const resendEmailToClientUser = createAsyncThunk(
  "clientUsers/resendEmailToClientUser",
  async (data, { dispatch }) => {
    const { loading, setLoading, client_user_id, isSelectedAll, ignoreIds } =
      data;
    if (loading) return;
    setLoading(true);

    const response = _resendEmailToClientUsers({
      client_user_id,
      isSelectedAll,
      ignoreIds,
    });
    response
      .then((res) => {
        const {
          data: { client_user, message },
        } = res;
        showSuccessToast(message);
        dispatch(deSelectAllUsers());
      })
      .catch((error) => {
        handleAPICatch(error);
      })
      .finally(() => {
        setLoading(false);
      });
  },
);

// delete user
export const deleteClientUserdata = createAsyncThunk(
  "clientUsers/deleteClientUserdata",
  async (data, { dispatch }) => {
    const {
      callback,
      loading,
      setLoading,
      clientUserId,
      isSelectedAll,
      ignoreIds,
    } = data;
    const _data = {
      clientUserId,
      isSelectedAll,
      ignoreIds,
    };
    if (loading) return;
    setLoading(true);
    const response = _deleteClientUser(_data);
    response
      .then((res) => {
        const {
          data: { data, message },
        } = res;
        dispatch(updateClientUserDelete(data));
        showSuccessToast(message);
        callback && callback();
        dispatch(deSelectAllUsers());
        dispatch(fetchUserAccessCounts());
      })
      .catch((error) => {
        handleAPICatch(error);
      })
      .finally(() => {
        setLoading(false);
      });
  },
);

// fetch all clientcontact list
export const fetchClientContactAlldata = createAsyncThunk(
  "clientProfile/fetchClientContactAlldata",
  async (data, { dispatch }) => {
    dispatch(setClientContactAllLoading(true));
    const response = _fetchClientContactAllList();
    response
      .then((res) => {
        const {
          data: { client_contact_list },
        } = res;
        dispatch(updateClientContactAll(client_contact_list));
      })
      .catch((error) => {
        // handleAPICatch(error);
      })
      .finally(() => {
        dispatch(setClientContactAllLoading(false));
      });
  },
);

const clientUsersSlice = createSlice({
  name: "clientUsers",
  initialState,
  reducers: {
    updateClientUsers: (state, action) => {
      state.clientUsers = action.payload;
    },
    updateClientContactAll: (state, action) => {
      state.clientContactAll = action.payload;
    },
    setClientUsersLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    setPaginationLoading(state, action) {
      state.isPaginationLoading = action.payload;
    },
    setClientContactAllLoading: (state, action) => {
      state.isClientContactAllLoading = action.payload;
    },
    updateSelectedClientUser: (state, action) => {
      state.selectedClientUser = action.payload;
    },
    updateFilters: (state, action) => {
      if (_.isEmpty(action.payload)) {
        state.filters = {
          search: state.filters.search,
          ...initialState.filters,
        };
        return;
      } else {
        if (action.payload.reset) {
          state.filters = initialState.filters;
        } else {
          state.filters = { ...state.filters, ...action.payload };
        }
      }
    },
    updateSorting: (state, action) => {
      state.sorting = action.payload;
    },
    deSelectAllUsers: (state) => {
      state.selectedClientUser = [];
      state.isSelectedAll = false;
      state.toggleSelected = !state.toggleSelected;
    },
    showFilterModal: (state, action) => {
      if (action.payload) {
        state.oldFilters = state.filters;
      }
      state.showFilters = action.payload;
    },
    updatePagination: (state, action) => {
      state.pagination = action.payload;
    },
    updateClientUserEdit: (state, action) => {
      state.EditClientUserData = action.payload;
    },
    updateClientUserDelete: (state, action) => {
      state.deleteClientUserdata = action.payload;
    },
    reStateFilters: (state, action) => {
      state.filters = state.oldFilters;
    },
    updateToggleSelected: (state, action) => {
      state.toggleSelected = !state.toggleSelected;
    },
    updateTotalCount: (state, action) => {
      state.totalCount = action.payload;
    },
    toggleSelectedAll: (state, action) => {
      state.isSelectedAll = action.payload;
    },
    toggleAddNewUserModal: (state, action) => {
      state.showAddNewUserModal = action.payload;
    },
    toggleEditNewUserModal: (state, action) => {
      state.showEditNewUserModal = action.payload;
    },
    togglePrimaryModal: (state, action) => {
      state.showPrimaryModal = action.payload;
    },
  },
});

export const {
  updateClientUsers,
  setClientUsersLoading,
  setPaginationLoading,
  updateSelectedClientUser,
  updateFilters,
  updateSorting,
  deSelectAllUsers,
  showFilterModal,
  setClientContactAllLoading,
  updatePagination,
  updateClientUserEdit,
  updateClientUserDelete,
  reStateFilters,
  updateToggleSelected,
  updateTotalCount,
  toggleSelectedAll,
  toggleAddNewUserModal,
  toggleEditNewUserModal,
  togglePrimaryModal,
  updateClientContactAll,
} = clientUsersSlice.actions;

export default clientUsersSlice.reducer;
